// 데모 현장 한 벌 — 가상의 「32평 아파트 전체 리모델링」. 금액은 실제 계산(calcQuote·calcChangeOrder·buildCurrentStatement)으로만 낸다.

import { calcChangeOrder } from "../change-order/calc";
import {
  buildCurrentStatement,
  type CurrentStatement,
  type StatementChangeOrderInput,
  type StatementGrouping,
  type StatementQuoteInput,
} from "../change-order/current-statement";
import { sourceLabelOf } from "../change-order/source";
import type { ChangeOrderDirection, ChangeOrderStatus } from "../change-order/types";
import type { ProjectMessage } from "../message/types";
import { amountsFromTotal } from "../money/vat";
import { SPACE_BUILTIN } from "../org/space-label";
import type { CustomerPhoto, CustomerProjectView } from "../project/customer-visible";
import type { ProjectPhaseStatus } from "../project/types";
import { calcQuote } from "../quote/calc";
import type { SnapshotData } from "../quote/types";
import type { TradeGroup } from "../template/trades";

export const DEMO_ORG_ID = "demo-org";
const QUOTE_ID = "demo-quote";
const PROJECT_ID = "demo-project";

export const DEMO_ORG = { name: "데모 인테리어", tel: "010-0000-0000", address: "서울특별시 예시구 데모로 1" };
export const DEMO_CUSTOMER = { name: "김데모", site_address: "예시아파트 101동 1001호 (32평)" };
export const DEMO_TITLE = "32평 아파트 전체 리모델링";
export const DEMO_ASSIGNEE = "최데모 실장";
export const DEMO_CONTRACT_NO = "C2609-001";
// 화면의 「오늘」 — 날짜를 고정해 둬야 언제 열어도 같은 화면이 나온다.
export const DEMO_TODAY = "2026-10-02";
export const DEMO_LINK_EXPIRES_AT = "2026-12-31T23:59:59+09:00";

interface ItemDef {
  id: string;
  scope: string;
  space: string | null;
  item: string;
  unit: string;
  qty: number;
  material: number;
  labor: number;
  note?: string;
}

interface TradeDef {
  id: string;
  trade: string;
  group: TradeGroup;
  name: string;
  condition?: string;
  items: ItemDef[];
}

const TRADES: TradeDef[] = [
  {
    id: "t-demolition",
    trade: "demolition",
    group: "BASIC",
    name: "철거 공사",
    items: [
      { id: "i-demo-all", scope: "전체", space: "COMMON", item: "내부 전체 철거", unit: "식", qty: 1, material: 0, labor: 3_800_000 },
      { id: "i-demo-waste", scope: "전체", space: "COMMON", item: "폐기물 처리", unit: "식", qty: 1, material: 0, labor: 1_200_000 },
    ],
  },
  {
    id: "t-plumbing",
    trade: "plumbing",
    group: "BASIC",
    name: "설비 공사",
    items: [
      { id: "i-plumb-water", scope: "바닥", space: "BATH", item: "욕실 방수", unit: "식", qty: 2, material: 300_000, labor: 450_000 },
      { id: "i-plumb-kitchen", scope: "배관", space: "KITCHEN", item: "주방 급배수 이설", unit: "식", qty: 1, material: 150_000, labor: 350_000 },
    ],
  },
  {
    id: "t-carpentry",
    trade: "carpentry",
    group: "BASIC",
    name: "목공 공사",
    items: [
      { id: "i-carp-ceiling", scope: "천장", space: "COMMON", item: "천장 평탄화", unit: "평", qty: 32, material: 25_000, labor: 35_000 },
      { id: "i-carp-wall", scope: "벽", space: "LIVING", item: "거실 아트월", unit: "식", qty: 1, material: 600_000, labor: 500_000 },
    ],
  },
  {
    id: "t-electric",
    trade: "electric",
    group: "BASIC",
    name: "전기설비 / 조명",
    items: [
      { id: "i-elec-light", scope: "천장", space: "COMMON", item: "매입등 교체", unit: "개", qty: 36, material: 18_000, labor: 12_000 },
      { id: "i-elec-switch", scope: "벽", space: "COMMON", item: "콘센트·스위치 교체", unit: "개", qty: 48, material: 6_000, labor: 4_000 },
    ],
  },
  {
    id: "t-tile",
    trade: "tile",
    group: "BASIC",
    name: "타일 공사",
    condition: "타일은 600×600 포세린 기준입니다.",
    items: [
      { id: "i-tile-bath", scope: "벽·바닥", space: "BATH", item: "욕실 벽·바닥 타일", unit: "㎡", qty: 38, material: 32_000, labor: 38_000 },
      { id: "i-tile-entrance", scope: "바닥", space: "ENTRANCE", item: "현관 바닥 타일", unit: "㎡", qty: 3, material: 40_000, labor: 40_000 },
    ],
  },
  {
    id: "t-wallpaper",
    trade: "wallpaper",
    group: "BASIC",
    name: "도배 공사",
    items: [{ id: "i-wall-silk", scope: "벽·천장", space: "COMMON", item: "실크 벽지", unit: "평", qty: 32, material: 30_000, labor: 25_000 }],
  },
  {
    id: "t-floor",
    trade: "floor",
    group: "STYLE",
    name: "마루 공사",
    items: [{ id: "i-floor-wood", scope: "바닥", space: "COMMON", item: "강마루", unit: "평", qty: 28, material: 95_000, labor: 30_000 }],
  },
  {
    id: "t-bath",
    trade: "bath",
    group: "STYLE",
    name: "바스 공사",
    items: [{ id: "i-bath-set", scope: "도기", space: "BATH", item: "욕실 도기·수전 세트", unit: "식", qty: 2, material: 1_300_000, labor: 250_000 }],
  },
  {
    id: "t-furniture",
    trade: "furniture",
    group: "STYLE",
    name: "가구 공사",
    items: [
      { id: "i-furn-kitchen", scope: "주방", space: "KITCHEN", item: "주방 상·하부장", unit: "m", qty: 5.4, material: 550_000, labor: 80_000 },
      { id: "i-furn-closet", scope: "수납", space: "BEDROOM", item: "붙박이장", unit: "자", qty: 12, material: 180_000, labor: 0 },
    ],
  },
];

const MGMT_FEE_RATE = 0.08;

const spaceLabelOf = (code: string | null): string | null =>
  code ? SPACE_BUILTIN.find((s) => s.code === code)?.label ?? null : null;

const calc = calcQuote({
  mgmt_fee_rate: MGMT_FEE_RATE,
  trades: TRADES.map((t) => ({
    group: t.group,
    items: t.items.map((i) => ({ qty: i.qty, material_unit_price: i.material, labor_unit_price: i.labor })),
  })),
});

// 공정 한 벌 — 견적서 시공일정과 「내 현장」 공정표가 같이 쓴다.
const PHASES: { name: string; trades: string[]; start_date: string; end_date: string; status: ProjectPhaseStatus }[] = [
  { name: "철거", trades: ["철거 공사"], start_date: "2026-09-14", end_date: "2026-09-16", status: "DONE" },
  { name: "설비·방수", trades: ["설비 공사"], start_date: "2026-09-17", end_date: "2026-09-21", status: "DONE" },
  { name: "목공", trades: ["목공 공사"], start_date: "2026-09-22", end_date: "2026-09-29", status: "DONE" },
  { name: "전기", trades: ["전기설비 / 조명"], start_date: "2026-09-28", end_date: "2026-09-30", status: "DONE" },
  { name: "타일·욕실", trades: ["타일 공사", "바스 공사"], start_date: "2026-10-01", end_date: "2026-10-08", status: "ACTIVE" },
  { name: "도배", trades: ["도배 공사"], start_date: "2026-10-12", end_date: "2026-10-14", status: "PENDING" },
  { name: "마루", trades: ["마루 공사"], start_date: "2026-10-15", end_date: "2026-10-17", status: "PENDING" },
  { name: "가구·마감", trades: ["가구 공사"], start_date: "2026-10-19", end_date: "2026-10-24", status: "PENDING" },
];

// 고객이 받은 견적서 리비전 2 — 실제 스냅샷과 같은 모양.
export const DEMO_QUOTE: SnapshotData = {
  org: DEMO_ORG,
  customer: DEMO_CUSTOMER,
  quote: {
    quote_no: "2609-001",
    version: 2,
    title: DEMO_TITLE,
    mgmt_fee_rate: MGMT_FEE_RATE,
    subtotal_basic: calc.subtotal_basic,
    subtotal_style: calc.subtotal_style,
    mgmt_fee: calc.mgmt_fee,
    gross: calc.gross,
    rounding_adjustment: calc.rounding_adjustment,
    total: calc.total,
    conditions: [
      "견적 유효기간은 발행일로부터 30일입니다.",
      "공사 중 추가·변경은 변경공사로 따로 확인받은 뒤 진행합니다.",
      "리비전 2 — 주방 상·하부장 길이와 욕실 타일 규격을 반영했습니다.",
    ],
    extra_notice: "에어컨 배관 이설·입주 청소·확장 공사는 이 견적에 포함되지 않습니다.",
    sent_at: "2026-09-03T10:00:00+09:00",
  },
  trades: TRADES.map((t, ti) => {
    const r = calc.trades[ti];
    return {
      group: t.group,
      trade: t.trade,
      name: t.name,
      sort_order: ti + 1,
      condition_text: t.condition ?? "",
      material_amount: r.material_amount,
      labor_amount: r.labor_amount,
      amount: r.amount,
      items: t.items.map((i, ii) => {
        const a = r.items[ii];
        return {
          scope: i.scope,
          space_code: i.space,
          space_label: spaceLabelOf(i.space),
          item: i.item,
          unit: i.unit,
          qty: i.qty,
          material_unit_price: i.material,
          labor_unit_price: i.labor,
          material_amount: a.material_amount,
          labor_amount: a.labor_amount,
          amount: a.amount,
          note: i.note ?? "",
          children: [],
        };
      }),
    };
  }),
  schedule: {
    start_date: "2026-09-14",
    end_date: "2026-10-24",
    total_workdays: 31,
    holidays_applied: true,
    phases: PHASES.map(({ name, trades, start_date, end_date }) => ({ name, trades, start_date, end_date })),
  },
};

// 계약금액 = 견적 총액(계약서에서 금액을 고치지 않은 경우).
export const DEMO_CONTRACT = amountsFromTotal(DEMO_QUOTE.quote.total);

const STATEMENT_QUOTE: StatementQuoteInput = {
  quote: { kind: "DETAIL", mgmt_fee: calc.mgmt_fee, gross: calc.gross, total: calc.total },
  trades: TRADES.map((t, ti) => ({ id: t.id, org_id: DEMO_ORG_ID, quote_id: QUOTE_ID, name: t.name, sort_order: ti + 1 })),
  items: TRADES.flatMap((t, ti) =>
    t.items.map((i, ii) => ({
      id: i.id,
      org_id: DEMO_ORG_ID,
      quote_id: QUOTE_ID,
      quote_trade_id: t.id,
      parent_id: null,
      sort_order: ii + 1,
      scope: i.scope,
      space_code: i.space,
      item: i.item,
      unit: i.unit,
      qty: i.qty,
      material_unit_price: i.material,
      labor_unit_price: i.labor,
      amount: calc.trades[ti].items[ii].amount,
    }))
  ),
};

interface ChangeLineDef {
  direction: ChangeOrderDirection;
  // 원 견적 줄에 연결하면 그 줄의 이름·공간·단위를 따른다 — 없으면 「추가 항목」.
  source?: string;
  item?: string;
  space?: string;
  unit?: string;
  qty: number;
  material: number;
  labor: number;
  note?: string;
}

const lineSource = (id: string): { trade: TradeDef; item: ItemDef } => {
  for (const trade of TRADES) {
    const item = trade.items.find((i) => i.id === id);
    if (item) return { trade, item };
  }
  throw new Error(`데모 견적에 없는 줄입니다: ${id}`);
};

const changeOrderOf = (id: string, seq: number, title: string, status: ChangeOrderStatus, lines: ChangeLineDef[]): StatementChangeOrderInput => {
  const c = calcChangeOrder(lines.map((l) => ({ direction: l.direction, qty: l.qty, material_unit_price: l.material, labor_unit_price: l.labor })));
  return {
    change_order: { id, seq, title, status, delta_total: c.delta_total, delta_supply: c.delta_supply, delta_vat: c.delta_vat },
    items: lines.map((l, i) => {
      const src = l.source ? lineSource(l.source) : null;
      const space = src ? src.item.space : l.space ?? null;
      return {
        id: `${id}-${i + 1}`,
        seq: i + 1,
        direction: l.direction,
        item: src ? src.item.item : l.item ?? "",
        unit: src ? src.item.unit : l.unit ?? "식",
        qty: l.qty,
        amount: c.lines[i].amount,
        note: l.note ?? "",
        source_quote_item_id: src ? src.item.id : null,
        trade_name: src ? src.trade.name : null,
        space_code: space,
        space_label: spaceLabelOf(space),
        source_label: src ? sourceLabelOf(src.item, src.trade.name) : null,
      };
    }),
  };
};

// 1차는 승인(내역서에 반영) · 2차는 고객 확인 대기(반영 안 됨).
export const DEMO_CHANGE_ORDERS: StatementChangeOrderInput[] = [
  changeOrderOf("demo-co-1", 1, "욕실 젠다이 · 현관 중문 추가", "ACCEPTED", [
    { direction: "ADD", source: "i-tile-bath", qty: 4, material: 32_000, labor: 38_000, note: "젠다이 상판 타일" },
    { direction: "ADD", item: "현관 중문 (3연동)", space: "ENTRANCE", unit: "식", qty: 1, material: 1_100_000, labor: 200_000 },
    { direction: "DEDUCT", source: "i-furn-closet", qty: 2, material: 180_000, labor: 0, note: "중문 자리만큼 붙박이장 축소" },
  ]),
  changeOrderOf("demo-co-2", 2, "작은방 매입등 추가", "SENT", [
    { direction: "ADD", source: "i-elec-light", qty: 6, material: 18_000, labor: 12_000 },
  ]),
];

export const DEMO_AWAITING = DEMO_CHANGE_ORDERS.filter((co) => co.change_order.status === "SENT").length;

export const demoStatement = (grouping: StatementGrouping): CurrentStatement =>
  buildCurrentStatement({
    contract: DEMO_CONTRACT,
    quote: STATEMENT_QUOTE,
    changeOrders: DEMO_CHANGE_ORDERS,
    spaces: SPACE_BUILTIN.map(({ code, label }) => ({ code, label })),
    grouping,
    audience: "customer",
  });

const photo = (id: string, log_date: string, caption: string, phase_name: string): CustomerPhoto => ({ id, log_date, caption, phase_name });

const DAYS: CustomerProjectView["days"] = [
  {
    date: "2026-10-01",
    logs: [{ id: "log-5", log_date: "2026-10-01", kind: "NOTE", body: "욕실 벽 타일을 붙이기 시작했습니다. 줄눈 색은 내일 샘플 보여 드리고 정하겠습니다." }],
    photos: [photo("p-7", "2026-10-01", "욕실 벽 타일", "타일·욕실"), photo("p-8", "2026-10-01", "타일 수평 확인", "타일·욕실")],
  },
  {
    date: "2026-09-30",
    logs: [
      {
        id: "log-4",
        log_date: "2026-09-30",
        kind: "EXTRA_WORK",
        body: "작은방 매입등 6개 추가 요청을 받아 변경공사 2차로 보내 드렸습니다. 확인해 주시면 진행합니다.",
      },
    ],
    photos: [photo("p-6", "2026-09-30", "전기 배선 정리", "전기")],
  },
  {
    date: "2026-09-26",
    logs: [{ id: "log-3", log_date: "2026-09-26", kind: "NOTE", body: "거실 아트월 목공을 마쳤습니다. 천장 평탄화도 끝났습니다." }],
    photos: [photo("p-4", "2026-09-26", "거실 아트월", "목공"), photo("p-5", "2026-09-26", "천장 평탄화", "목공")],
  },
  {
    date: "2026-09-18",
    logs: [{ id: "log-2", log_date: "2026-09-18", kind: "ISSUE", body: "주방 급수관이 낡아 이설하면서 같이 교체했습니다. 추가 비용은 없습니다." }],
    photos: [photo("p-3", "2026-09-18", "욕실 방수 1차", "설비·방수")],
  },
  {
    date: "2026-09-16",
    logs: [{ id: "log-1", log_date: "2026-09-16", kind: "NOTE", body: "철거와 폐기물 반출을 마쳤습니다." }],
    photos: [photo("p-1", "2026-09-16", "철거 후 거실", "철거"), photo("p-2", "2026-09-16", "철거 후 주방", "철거")],
  },
];

export const DEMO_PROJECT: CustomerProjectView = {
  title: DEMO_TITLE,
  status: "IN_PROGRESS",
  planned_start_date: "2026-09-14",
  planned_end_date: "2026-10-24",
  started_at: "2026-09-14",
  completion_date: null,
  warranty_end_date: null,
  assignee_name: DEMO_ASSIGNEE,
  phases: PHASES.map(({ name, start_date, end_date, status }) => ({ name, start_date, end_date, status })),
  days: DAYS,
  photos: DAYS.flatMap((d) => d.photos),
};

const message = (n: number, created_at: string, author_kind: ProjectMessage["author_kind"], body: string): ProjectMessage => ({
  id: `demo-msg-${n}`,
  org_id: DEMO_ORG_ID,
  created_at,
  project_id: PROJECT_ID,
  author_kind,
  author_member_id: author_kind === "STAFF" ? "demo-member" : null,
  author_name: author_kind === "STAFF" ? DEMO_ASSIGNEE : DEMO_CUSTOMER.name,
  body,
  edited_at: null,
  deleted_at: null,
  photo_path: null,
  photo_mime: null,
  photo_size_bytes: null,
  photo_checked_at: null,
  photo_missing_at: null,
});

export const DEMO_MESSAGES: ProjectMessage[] = [
  message(1, "2026-09-26T18:10:00+09:00", "CUSTOMER", "아트월 사진 잘 봤어요. 작은방에도 매입등을 몇 개 더 달 수 있을까요?"),
  message(2, "2026-09-30T17:40:00+09:00", "STAFF", "6개 추가로 변경공사 2차를 보내 드렸습니다. 금액 확인하시고 승인해 주시면 전기 마감 때 같이 달겠습니다."),
  message(3, "2026-10-01T19:05:00+09:00", "CUSTOMER", "네, 내일 확인해 볼게요."),
];
